import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, XCircle, Mail, Clock, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";

const EmailVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { verifyEmail, checkTokenForEmail, resendVerificationEmail } = useAuth();

  const [status, setStatus] = useState("verifying"); // verifying, success, expired, error
  const [message, setMessage] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isResending, setIsResending] = useState(false);

  // Verify email on component mount
  useEffect(() => {
    if (token) {
      handleVerifyEmail();
    } else {
      setStatus("error");
      setMessage("Invalid verification link");
    }
  }, [token]);

  const handleVerifyEmail = async () => {
    try {
      const result = await verifyEmail(token);

      if (result.success) {
        setStatus("success");
        setMessage(result.message);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/auth?mode=login", {
            state: { 
              message: "Email verified successfully! You can now log in.", 
              type: "success" 
            }
          });
        }, 3000);
      } else {
        if (result.expired) {
          // Check if we can get the user's email for resend option
          await handleCheckTokenForEmail();
          setStatus("expired");
        } else {
          setStatus("error");
        }
        setMessage(result.message);
      }
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("error");
      setMessage("An error occurred during verification. Please try again.");
    }
  };

  const handleCheckTokenForEmail = async () => {
    try {
      const result = await checkTokenForEmail(token);
      
      if (result.success && result.email) {
        setUserEmail(result.email);
      }
    } catch (error) {
      console.error("Token check error:", error);
    }
  };

  const handleResendVerification = async () => {
    if (!userEmail) return;

    setIsResending(true);
    try {
      const result = await resendVerificationEmail(userEmail);

      if (result.success) {
        setMessage(result.message || "New verification email sent! Please check your email.");
        setStatus("success");
      } else {
        setMessage(result.message || "Failed to resend verification email. Please try again.");
      }
    } catch (error) {
      console.error("Resend error:", error);
      setMessage("Failed to resend verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case "verifying":
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 relative">
              <div className="w-16 h-16 border-4 border-igbanwo-mint border-t-transparent rounded-full animate-spin"></div>
              <Mail className="w-8 h-8 text-igbanwo-mint absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
            <h1 className="text-2xl font-bold mb-4">Verifying Your Email...</h1>
            <p className="text-muted-foreground">
              Please wait while we verify your email address.
            </p>
          </div>
        );

      case "success":
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-green-600 mb-4">Email Verified!</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Redirecting you to login in 3 seconds...
              </p>
              <Button 
                onClick={() => navigate("/auth?mode=login")}
                className="bg-igbanwo-mint hover:bg-igbanwo-mint/90 text-white"
              >
                Go to Login Now
              </Button>
            </div>
          </div>
        );

      case "expired":
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-orange-600 mb-4">Link Expired</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            
            {userEmail && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  We can send a new verification email to: <strong>{userEmail}</strong>
                </p>
                <Button 
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="bg-igbanwo-mint hover:bg-igbanwo-mint/90 text-white"
                >
                  {isResending ? (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Sending...
                    </div>
                  ) : (
                    "Send New Verification Email"
                  )}
                </Button>
              </div>
            )}
            
            <div className="mt-6">
              <Link 
                to="/auth?mode=login" 
                className="text-igbanwo-mint hover:text-igbanwo-mint/80 text-sm"
              >
                Back to Login
              </Link>
            </div>
          </div>
        );

      case "error":
      default:
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-red-600 mb-4">Verification Failed</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Please try registering again or contact support if the problem persists.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline"
                  onClick={() => navigate("/auth?mode=register")}
                  className="border-igbanwo-mint text-igbanwo-mint hover:bg-igbanwo-mint hover:text-white"
                >
                  Register Again
                </Button>
                <Button 
                  onClick={() => navigate("/auth?mode=login")}
                  className="bg-igbanwo-mint hover:bg-igbanwo-mint/90 text-white"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16 flex items-center justify-center">
        <div className="container px-4 mx-auto">
          <div className="max-w-md mx-auto">
            <div className="bg-white p-8 rounded-xl border border-border/60 shadow-sm">
              {renderContent()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmailVerification;